import React, { useState } from 'react';
import { BsFilePerson } from 'react-icons/bs';
import { FaDesktop, FaGavel } from 'react-icons/fa';
import { BsGearFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import Banca, { BancaUtils, StatusBanca, TipoBancaNome } from '../../../model/banca/Banca';
import { BancaNovaResponse } from '../../../model/banca/BancasNovaResponse';
import { getButtonConfigFromBackend, shouldShowCandidatosColumn, shouldShowColumnFrontend } from '../utils/bancaButtonConfigUtils';
import ActionButton from './ActionButton';
import BancaActions from './BancaActions';
import CandidatosActions from './CandidatosActions';
import StatusBancaButton from './StatusBancaButton';
import AlterarFormaModal from './AlterarFormaModal';
import AlterarIntegracaoModal from './AlterarIntegracaoModal';
import PublicarIntegracaoSigaModal from './PublicarIntegracaoSigaModal';
import { BancaViewModel } from '../../../viewmodel/banca/BancaViewModel';
import PermissaoWrapper from '../../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';
import CandidatoViewModel from '../../../viewmodel/CandidatoViewModel';
import AdminActionsModal from './AdminActionsModal';
import HelpTooltip from './HelpTooltip';

interface TabelaBancasProps {
  bancas: BancaNovaResponse[];
  loading: boolean;
  onBancaUpdated: () => void;
  sortedColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  onStatusClick: (status: StatusBanca) => void;
  onComposicaoClick?: (banca: any) => void;
  onDataClick?: (banca: any) => void;
  onConvocarClick?: (banca: any) => void;
  onSalaClick?: (banca: any) => void;
  onEmergenciaClick?: (banca: any) => void;
  onAtaClick?: (banca: any) => void;
  onCancelarClick?: (banca: any) => void;
  onFecharClick?: (banca: any) => void;
  onAbrirIntegracaoClick?: (banca: any) => void;
  onFinalizarIntegracaoClick?: (banca: any) => void;
  onEnviarTermosClick?: (banca: any) => void;
  onAlterarStatusClick?: (banca: any) => void;
  processoId?: string;
}

const TabelaBancas: React.FC<TabelaBancasProps> = ({
  bancas,
  loading,
  onBancaUpdated,
  sortedColumn,
  sortDirection,
  onSort,
  onStatusClick,
  onComposicaoClick,
  onDataClick,
  onConvocarClick,
  onSalaClick,
  onEmergenciaClick,
  onAtaClick,
  onCancelarClick,
  onFecharClick,
  onAbrirIntegracaoClick,
  onFinalizarIntegracaoClick,
  onEnviarTermosClick,
  onAlterarStatusClick,
  processoId
}) => {
  const navigate = useNavigate();
  const [showAlterarFormaModal, setShowAlterarFormaModal] = useState(false);
  const [showAlterarIntegracaoModal, setShowAlterarIntegracaoModal] = useState(false);
  const [showPublicarSigaModal, setShowPublicarSigaModal] = useState(false);
  const [bancaSelecionada, setBancaSelecionada] = useState<any>(null);
  const [showAdminActionsModal, setShowAdminActionsModal] = useState(false);

  const handleAlterarFormaClick = (banca: any) => {
    setBancaSelecionada(banca);
    setShowAlterarFormaModal(true);
  };

  const handleAlterarIntegracaoClick = (banca: any) => {
    setBancaSelecionada(banca);
    setShowAlterarIntegracaoModal(true);
  };

  const handlePublicarSigaClick = (banca: any) => {
    setBancaSelecionada(banca);
    setShowPublicarSigaModal(true);
  };

  const handleOpenAdminActionsModal = (banca: any) => {
    setBancaSelecionada(banca);
    setShowAdminActionsModal(true);
  };

  const handleBaixarCandidatosResumo = async () => {
    if (!processoId) return;
    try {
      const data = await CandidatoViewModel.getCandidatosResumo(processoId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidatos-resumo-${data.identificadorProcesso}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar resumo de candidatos:', error);
    }
  };

  const handleAlterarFormaConfirm = async () => {
    if (!bancaSelecionada) return;
    await BancaViewModel.alternarFormaRealizacao(bancaSelecionada.id);
    onBancaUpdated();
  };

  const handleAlterarIntegracaoConfirm = async () => {
    if (!bancaSelecionada) return;
    await BancaViewModel.alternarHasIntegracao(bancaSelecionada.id);
    onBancaUpdated();
  };

  const handlePublicarSigaConfirm = async () => {
    if (!bancaSelecionada) return;
    await BancaViewModel.publicarEventoIntegracaoSiga(bancaSelecionada.id);
  };

  const getTipoBancaIcon = (tipoBanca: string) => {
    switch (tipoBanca) {
      case TipoBancaNome.FACIAL:
        return <BsFilePerson style={{ fontSize: '20px', color: '#2F4F4F' }} />;
      case TipoBancaNome.RECURSAL:
        return <FaGavel style={{ fontSize: '20px', color: '#2F4F4F' }} />;
      case TipoBancaNome.VIRTUAL:
        return <FaDesktop style={{ fontSize: '20px', color: '#2F4F4F' }} />;
      default:
        return null;
    }
  };



  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-container">
          <div>Carregando bancas...</div>
        </div>
      </div>
    );
  }

  if (bancas.length === 0) {
    return (
      <div className="table-container">
        <div className="empty-state">
          Nenhuma banca encontrada.
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-responsive">
        <table className="bancas-table">
          <thead>
            <tr>
              <th onClick={() => onSort('tipoBanca')} style={{ cursor: 'pointer' }}>
                Tipo
                {sortedColumn === 'tipoBanca' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
              </th>
              <th onClick={() => onSort('data')} style={{ cursor: 'pointer' }}>
                Data
                {sortedColumn === 'data' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
              </th>
              <th>Forma <HelpTooltip text="Presencial ou Remoto. Clique no ícone para alterar (apenas em bancas Indefinidas ou Planejadas)." /></th>
              <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                <th>Admin</th>
              </PermissaoWrapper>
              <th>Presidente(a)</th>
              <th style={{ textAlign: 'left' }}>Editais/Chamadas <HelpTooltip text="Editais e chamadas associados a esta banca." /></th>
              <th onClick={() => onSort('status')} style={{ cursor: 'pointer' }}>
                Status
                {sortedColumn === 'status' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
              </th>
              <th>Ações <HelpTooltip text="Sala, Data, Composição, Convocação, Emergência e ATA. Cada botão fica disponível conforme o status da banca." /></th>
              {shouldShowCandidatosColumn(bancas) && <th>Candidatos</th>}
              {shouldShowColumnFrontend(bancas, 'cancelar') && <th>Cancelar</th>}
              {shouldShowColumnFrontend(bancas, 'fechar') && <th>Fechar</th>}
              {shouldShowColumnFrontend(bancas, 'finalizar') && <th>Finalizar</th>}
              {shouldShowColumnFrontend(bancas, 'abrirIntegracao') && <th>Abrir Integração</th>}
              {shouldShowColumnFrontend(bancas, 'finalizarIntegracao') && <th>Concluir Integração</th>}
            </tr>
          </thead>
          <tbody>
            {bancas.map(banca => (
              <tr key={banca.id}>
                <td style={{ textAlign: 'center' }}>
                  {getTipoBancaIcon(banca.tipoBanca?.nome)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {BancaUtils.formatarDataDeBanca(banca.dataDeBanca)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleAlterarFormaClick(banca)}
                      disabled={banca.status !== StatusBanca.INDEFINIDA && banca.status !== StatusBanca.PLANEJADA}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: (banca.status === StatusBanca.INDEFINIDA || banca.status === StatusBanca.PLANEJADA) ? 'pointer' : 'not-allowed',
                        opacity: (banca.status === StatusBanca.INDEFINIDA || banca.status === StatusBanca.PLANEJADA) ? 1 : 0.5,
                        padding: '4px 8px',
                        fontSize: '18px'
                      }}
                      title={banca.formaRealizacao === 'REMOTO' ? 'Remoto' : 'Presencial'}
                    >
                      <i className={banca.formaRealizacao === 'REMOTO' ? 'bi bi-camera-video' : 'bi bi-people'}></i>
                    </button>
                  </div>
                </td>
                <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleOpenAdminActionsModal(banca)}
                        style={{
                          width: '34px',
                          height: '34px',
                          border: '1px solid #6c757d',
                          borderRadius: '4px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Ações administrativas"
                      >
                        <BsGearFill />
                      </button>
                    </div>
                  </td>
                </PermissaoWrapper>
                <td style={{ textAlign: 'center' }}>
                  {banca.presidente}
                </td>
                <td style={{ textAlign: 'left', maxWidth: '200px' }}>
                  {banca.editais && banca.editais.length > 0 ? (
                    <div style={{ fontSize: '12px' }}>
                      {banca.editais.map((edital, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          <strong>{edital.editalNome}</strong>
                          {edital.hasChamadas && edital.numeroChamada && (
                            <span style={{ color: '#28a745', marginLeft: '6px' }}>
                              ({edital.numeroChamada}ª chamada)
                            </span>
                          )}
                          {edital.hasChamadas && !edital.numeroChamada && (
                            <span style={{ color: '#6c757d', marginLeft: '6px' }}>
                              (sem chamada definida)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>
                      Nenhum edital associado
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <StatusBancaButton
                    status={BancaUtils.getRealStatusBanca(banca)}
                    onClick={() => onStatusClick(BancaUtils.getRealStatusBanca(banca))}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <BancaActions banca={banca} onComposicaoClick={onComposicaoClick} onDataClick={onDataClick} onConvocarClick={onConvocarClick} onSalaClick={onSalaClick} onEmergenciaClick={onEmergenciaClick} onAtaClick={onAtaClick} />
                </td>
                {shouldShowCandidatosColumn(bancas) && (
                  <td style={{ textAlign: 'center' }}>
                    <CandidatosActions banca={banca} processoId={processoId} onEnviarTermosClick={onEnviarTermosClick} />
                  </td>
                )}
                {shouldShowColumnFrontend(bancas, 'cancelar') && (
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton
                      config={getButtonConfigFromBackend(banca, 'cancelar')}
                      onClick={() => onCancelarClick?.(banca)}
                    />
                  </td>
                )}
                {shouldShowColumnFrontend(bancas, 'fechar') && (
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton
                      config={getButtonConfigFromBackend(banca, 'fechar')}
                      onClick={() => onFecharClick?.(banca)}
                    />
                  </td>
                )}
                {shouldShowColumnFrontend(bancas, 'finalizar') && (
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton config={getButtonConfigFromBackend(banca, 'finalizar')} />
                  </td>
                )}
                {shouldShowColumnFrontend(bancas, 'abrirIntegracao') && (
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton
                      config={getButtonConfigFromBackend(banca, 'abrirIntegracao')}
                      onClick={() => onAbrirIntegracaoClick?.(banca)}
                    />
                  </td>
                )}
                {shouldShowColumnFrontend(bancas, 'finalizarIntegracao') && (
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton
                      config={getButtonConfigFromBackend(banca, 'finalizarIntegracao')}
                      onClick={() => onFinalizarIntegracaoClick?.(banca)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAlterarFormaModal && bancaSelecionada && (
        <AlterarFormaModal
          banca={bancaSelecionada}
          onClose={() => setShowAlterarFormaModal(false)}
          onConfirm={handleAlterarFormaConfirm}
        />
      )}

      {showAlterarIntegracaoModal && bancaSelecionada && (
        <AlterarIntegracaoModal
          banca={bancaSelecionada}
          onClose={() => setShowAlterarIntegracaoModal(false)}
          onConfirm={handleAlterarIntegracaoConfirm}
        />
      )}

      {showPublicarSigaModal && bancaSelecionada && (
        <PublicarIntegracaoSigaModal
          banca={bancaSelecionada}
          onClose={() => setShowPublicarSigaModal(false)}
          onConfirm={handlePublicarSigaConfirm}
        />
      )}

      {showAdminActionsModal && bancaSelecionada && (
        <AdminActionsModal
          isOpen={showAdminActionsModal}
          onClose={() => setShowAdminActionsModal(false)}
          banca={bancaSelecionada}
          processoId={processoId}
          onToggleIntegracao={handleAlterarIntegracaoClick}
          onGerenciarVotacao={(banca) => navigate(`/bancas/${banca.id}/votacao-admin`)}
          onPublicarSiga={handlePublicarSigaClick}
          onBaixarResumo={handleBaixarCandidatosResumo}
          onAlterarStatus={(banca) => onAlterarStatusClick?.(banca)}
        />
      )}
    </div>
  );
};

export default TabelaBancas;
